import { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Upload, RotateCw, Save, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useImageModeration } from '@/hooks/useImageModeration';

interface AvatarEditorProps {
  isOpen: boolean;
  onClose: () => void;
  currentAvatar?: string;
  onAvatarUpdated: (url: string) => void;
}

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area,
  outputSize: number = 512
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  canvas.width = outputSize;
  canvas.height = outputSize;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  return canvas.toDataURL('image/webp', 0.9);
};

export function AvatarEditor({ isOpen, onClose, currentAvatar, onAvatarUpdated }: AvatarEditorProps) {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { moderateImage, isChecking: isModeratingImage } = useImageModeration();

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    
    if (file.size > maxSize) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "Please select an image under 5MB.",
      });
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      toast({
        variant: "destructive", 
        title: "Invalid file type",
        description: "Please select a JPEG, PNG, or WebP image.",
      });
      return;
    }

    // Moderate image before allowing selection
    const isApproved = await moderateImage(file);
    if (!isApproved) {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result as string);
    });
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels || !user) return;

    setIsUploading(true);
    try {
      // Generate cropped images
      const croppedImage512 = await getCroppedImg(imageSrc, croppedAreaPixels, 512);
      const croppedImage128 = await getCroppedImg(imageSrc, croppedAreaPixels, 128);

      // Convert to blobs
      const blob512 = await fetch(croppedImage512).then(res => res.blob());
      const blob128 = await fetch(croppedImage128).then(res => res.blob());

      const timestamp = Date.now();
      
      // Upload both sizes
      const { error: error512 } = await supabase.storage
        .from('avatars')
        .upload(`${user.id}/avatar-512.webp`, blob512, {
          upsert: true,
          contentType: 'image/webp'
        });

      if (error512) throw error512;

      const { error: error128 } = await supabase.storage
        .from('avatars')  
        .upload(`${user.id}/avatar-128.webp`, blob128, {
          upsert: true,
          contentType: 'image/webp'
        });

      if (error128) throw error128;

      // Get public URLs with cache busting
      const { data: { publicUrl: url512 } } = supabase.storage
        .from('avatars')
        .getPublicUrl(`${user.id}/avatar-512.webp?v=${timestamp}`);

      const { data: { publicUrl: url128 } } = supabase.storage
        .from('avatars')
        .getPublicUrl(`${user.id}/avatar-128.webp?v=${timestamp}`);

      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          image: url512,
          image_thumb: url128
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      toast({
        title: "Avatar updated",
        description: "Your profile picture has been updated successfully.",
      });

      onAvatarUpdated(url512);
      onClose();
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: "Failed to update your avatar. Please try again.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user) return;
    
    setIsUploading(true);
    try {
      // Update profile to remove avatar
      const { error } = await supabase
        .from('profiles')
        .update({ image: null, image_thumb: null })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: "Avatar removed",
        description: "Your profile picture has been removed.",
      });

      onAvatarUpdated('');
      onClose();
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast({
        variant: "destructive",
        title: "Failed to remove avatar",
        description: "Please try again.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetEditor = () => {
    setImageSrc('');
    setCrop({ x: 0, y: 0 });
    setRotation(0);
    setZoom(1);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetEditor();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" aria-describedby="avatar-editor-description">
        <DialogHeader>
          <DialogTitle>Edit Profile Picture</DialogTitle>
        </DialogHeader>
        
        <div id="avatar-editor-description" className="sr-only">
          Upload and crop your profile picture. Use the controls to zoom and rotate your image.
        </div>

        <div className="space-y-4">
          {!imageSrc ? (
            <div className="space-y-4">
              <div 
                className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PNG, JPG or WebP (max 5MB)
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />

              {currentAvatar && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Current avatar:</p>
                  <div className="flex items-center gap-3">
                    <img src={currentAvatar} alt="Current avatar" className="w-12 h-12 rounded-full object-cover ring-2 ring-butter" />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleRemoveAvatar}
                      disabled={isUploading}
                    >
                      Remove Photo
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative w-full h-64 bg-muted rounded-lg overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  rotation={rotation}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape="round"
                  showGrid={false}
                />
              </div>

              <div className="space-y-3">
                <div>
                  <Label>Zoom</Label>
                  <Slider
                    value={[zoom]}
                    min={1}
                    max={3}
                    step={0.1}
                    onValueChange={(values) => setZoom(values[0])}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Rotation</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Slider
                      value={[rotation]}
                      min={0}
                      max={360}
                      step={1}
                      onValueChange={(values) => setRotation(values[0])}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRotation((prev) => (prev + 90) % 360)}
                    >
                      <RotateCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={resetEditor} className="flex-1">
                  <X className="h-4 w-4 mr-2" />
                  Change Photo
                </Button>
                <Button onClick={handleSave} disabled={isUploading || isModeratingImage} className="flex-1">
                  <Save className="h-4 w-4 mr-2" />
                  {isUploading || isModeratingImage ? (isModeratingImage ? 'Checking...' : 'Saving...') : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}